import './StationDetail.css';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
// Hooks
import { useStationDetail } from '../../hooks/useStationDetail';
import useRealtimeScheduler from '../../hooks/useRealtimeScheduler';
import { useSetUpList } from '../../hooks/queries/setUpQueries';
// Icons
import EditIcon from '../../assets/icons/edit.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
// Naver Maps
import { useNavermaps } from "react-naver-maps";
import {
  Container as MapDiv,
  NaverMap,
  Marker,
  useListener,
} from "react-naver-maps";
// Components
import StationChart from './components/StationChart';
import StationUpdateSideModal from './components/StationUpdateSideModal';
import StationDeleteModal from '../StationManagement/component/StationDeleteModal';
const StationDetail = () => {
  // Params
  const { stationSysId } = useParams();
  // Navigate
  const navigate = useNavigate();
  const location = useLocation();
  // States
  const [activeTab, setActiveTab] = useState('stat');
  const [editModalOn, setEditModalOn] = useState(false);
  const [deleteModalOn, setDeleteModalOn] = useState(false);

  // 뒤로가기 핸들러
  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1); // 프로젝트 내에서 접근한 경우 뒤로가기
    } else {
      navigate('/station-management'); // URL로 직접 접근한 경우 목록으로
    }
  };
  
  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;
  const defaultChargingDemandPredictionModuleName = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_NAME')?.Value;
  const defaultChargingDemandPredictionModuleVersion = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_VERSION')?.Value;

  // React Query Hooks
  const {
    station,
    powerMeterDemand,
    drResourceData,
    isLoading,
    error,
    powerMeterDemandLoading,
    powerMeterDemandError,
    getPowerMeterDemand,
    cblData,
    cblLoading,
    cblError,
    refetchCbl,
    predictionCblData,
    predictionCblLoading,
    predictionCblError,
    refetchPredictionCbl,
    drHistoryLatestData,
    powerMeterDemandDrEvent,
    powerMeterDemandYesterday,
  } = useStationDetail(stationSysId);

  // Realtime Scheduler
  const {
    getCurrentTime,
    nextUpdateTime,
    lastUpdateTime,
    isUpdating,
    manualUpdate
  } = useRealtimeScheduler(
    async (updateTime) => {
      // Refresh PowerMeterDemand
      await getPowerMeterDemand();
      // Refresh CBL
      await refetchCbl();
      // 예측 CBL 데이터 새로고침
      await refetchPredictionCbl();
    },
    {
      enabled: !!stationSysId,
      immediate: false
    }
  );

  // Naver Maps
  const navermaps = useNavermaps();
  let [map, setMap] = useState();
  const [pos, setPos] = useState(null);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'using':
        return { text: '사용', color: '#29D668', dotColor: '#29D668', bgColor: '#E5FAEA' };
      case 'waiting':
        return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
      case 'unavailable':
        return { text: '불가', color: '#FEC02E', dotColor: '#FEC02E', bgColor: '#FFFDE7' };
      default:
        return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
    }
  };
  // 충전기 상태코드를 3가지 카테고리로 매핑하는 헬퍼 함수
  const getChargerStatusCategory = (statusCode) => {
    switch (statusCode) {
      case 2: // 충전대기
      case 6: // 예약중
        return 'waiting';
      case 3: // 충전중
        return 'using';
      case 0: // 알수없음
      case 1: // 통신이상
      case 4: // 운영중지
      case 5: // 점검중
      case 9: // 상태미확인
      default:
        return 'unavailable';
    }
  };
  const onEditSuccess = () => {
    // 페이지 새로고침으로 최신 데이터 로드
    window.location.reload();
  };

  const onDeleteSuccess = () => {
    // 삭제 성공 시 목록 페이지로 이동
    navigate('/station-management');
  };


  if (isLoading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p className='text-b1-400 text-base'>충전소 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-error">
        <h2 className='text-b1-700 text-base'>오류가 발생했습니다</h2>
        <div className="flex flex gap-5">
          <button onClick={() => getPowerMeterDemand()} className="btn big-btn btn-primary">
            다시 시도
          </button>
          <button onClick={() => navigate('/station-management')} className="btn big-btn btn-style-5">
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }


  if (!station) {
    return (
      <div className="detail-not-found">
        <h2 className='text-b1-700 text-base'>충전소를 찾을 수 없습니다</h2>
        <p className='text-b1-400 text-base'>요청하신 충전소가 존재하지 않거나 삭제되었습니다.</p>
        <button onClick={() => navigate('/station-management')} className="btn btn-primary">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-container">
      <div className="detail-page-content">
          <div className='detail-page-content-divide-container'>
            <div className='detail-page-content-divide-content left'>
              {/* LEFT HEADER */}
              <div className='flex align-center mb-5'>
                <button className='btn icon-btn' onClick={handleBack} title="뒤로가기">
                  <ChevronLeftIcon color='var(--gray-600)'/>
                  <span className='text-b2-700 text-gray-600'>뒤로가기</span>
                </button>
              </div>
              <div className='detail-page-content-divide-content-header flex align-center gap-5'>
                <h3 className='text-b1-700 text-primary'>{station.CpoStationId}</h3>
                <button className='btn icon-btn' onClick={() => setEditModalOn(true)}>
                  <EditIcon color='var(--gray-600)'/>
                </button>
                <button className='btn icon-btn' onClick={() => setDeleteModalOn(true)}>
                  <DeleteIcon color='var(--gray-600)'/>
                </button>
              </div>
              {/* LEFT CONTENT */}
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <h3 className='text-b1-700 text-base'>기본 정보</h3>
                <table className='gray-table'>
                  <tr>
                    <th width="150px">충전소 이름</th>
                    <td>{station.CpoStationName}</td>
                  </tr>
                  <tr>
                    <th width="150px">고유 식별자</th>
                    <td>{station.CpoStationId}</td>
                  </tr>
                  <tr>
                    <th width="150px">주소</th>
                    <td>{station.Address}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20 relative'>
                <h3 className='text-b1-700 text-base'>EV 충전소 위치보기</h3>
                <div style={{ width: '100%', height: '300px' }}>
                  <MapDiv
                    className="w-full h-full"
                    error={<p className="p-4 text-center">지도 로드 오류</p>}
                    loading={<p className="p-4 text-center">지도 로딩 중...</p>}
                  >
                    <NaverMap
                      ref={setMap}
                      defaultCenter={pos || new navermaps.LatLng(station.Lat, station.Lng)}
                      defaultZoom={17}
                      disableKineticPan={false}
                      scaleControl={true}
                      draggable={false}
                      pinchZoom={false}
                      scrollWheel={false}
                      keyboardShortcuts={false}
                      disableDoubleTapZoom={true}
                      disableDoubleClickZoom={true}
                      disableTwoFingerTapZoom={true}
                      zoomControl={false}
                      onBoundsChanged={(e) => {}}
                      minZoom={9}
                      maxZoom={21}
                    >
                      <Marker
                        position={new navermaps.LatLng(station.Lat, station.Lng)}
                        icon={{
                          content: `<div style="background-color: #f44336; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>`,
                          size: new navermaps.Size(20, 20),
                          anchor: new navermaps.Point(10, 10),
                        }}
                      />
                    </NaverMap>
                  </MapDiv>
                </div>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <h3 className='text-b1-700 text-base'>계량기 정보</h3>
                <table className='gray-table'>
                  <tr>
                    <th width="150px">계량기 계약번호</th>
                    <td>{station.PowerMeterContractNumber}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex align-center justify-between'>
                  <h3 className='text-b1-700 text-base'>DR자원 정보</h3>
                  {station?.DrResourceSysId && (
                  <p className='text-b2-400 text-gray-600 cursor-pointer text-underline' onClick={() => {navigate(`/dr-management/detail/${station?.DrResourceSysId}`, { state: { from: 'detail' } })}}>자세히보기</p>
                  )}
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="150px">이름</th>
                    <td>{drResourceData ? drResourceData.ResourceName : "DR자원 미할당"}</td>
                  </tr>
                  <tr>
                    <th width="150px">지역코드</th>
                    <td>{drResourceData ? drResourceData.RegionCode : "-"}</td>
                  </tr>
                  <tr>
                    <th width="150px">할당된 충전소 수</th>
                    <td>{drResourceData ? drResourceData.Stations.length : "-"}</td>
                  </tr>
                  <tr>
                    <th width="150px">할당된 충전기 수</th>
                    <td>{drResourceData ? drResourceData.Chargers.length : "-"}</td>
                  </tr>
                </table>
              </div>

              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <h3 className='text-b1-700 text-base'>CBL 연산 모듈 정보</h3>
                <table className='gray-table'>
                  <tr>
                    <th width="150px">모듈 이름</th>
                    <td>{drResourceData ? drResourceData.AppliiedCblAlgorithm && drResourceData.AppliiedCblAlgorithm !== "" ? drResourceData.AppliiedCblAlgorithm : `(기본) ${defaultCblModuleName}` : `(기본) ${defaultCblModuleName}`}</td>
                  </tr>
                  <tr>
                    <th width="150px">버전</th>
                    <td>{drResourceData ? drResourceData.AppliedCblAlgorithmVersion && drResourceData.AppliedCblAlgorithmVersion !== "" ? drResourceData.AppliedCblAlgorithmVersion : defaultCblModuleVersion : defaultCblModuleVersion}</td>
                  </tr>
                </table>
              </div>

              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex flex-col gap-2'>
                  <h3 className='text-b1-700 text-base'>일일 충전량 예측 연산 모듈 정보</h3>
                  <p className='text-b2-400 text-gray-600'>적용되는 모듈은 시스템 설정을 통해 변경 가능합니다.</p>
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="150px">모듈 이름</th>
                    <td>{defaultChargingDemandPredictionModuleName || "-"}</td>
                  </tr>
                  <tr>
                    <th width="150px">버전</th>
                    <td>{defaultChargingDemandPredictionModuleVersion || "-"}</td>
                  </tr>
                </table>
              </div>

            </div>
            <div className='detail-page-content-divide-content right'>
              <div className="station-detail-tabs-container">
                <div className="tabs station-detail-tabs">
                  <button
                    className={`tab ${activeTab === 'stat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stat')}
                  >
                    통계
                  </button>
                  <button
                    className={`tab ${activeTab === 'charger' ? 'active' : ''}`}
                    onClick={() => setActiveTab('charger')}
                  >
                    충전기 목록
                  </button>
                </div>
              </div>
              {/* RIGHT CONTENT */}
              {activeTab === 'stat' && (
              <StationChart
                powerMeterDemand={powerMeterDemand}
                powerMeterDemandLoading={powerMeterDemandLoading}
                powerMeterDemandError={powerMeterDemandError}
                cblData={cblData}
                cblLoading={cblLoading}
                cblError={cblError}
                predictionCblData={predictionCblData}
                predictionCblLoading={predictionCblLoading}
                predictionCblError={predictionCblError}
                refetchPredictionCbl={refetchPredictionCbl}
                powerMeterDemandYesterday={powerMeterDemandYesterday}
                // 실시간 업데이트 정보
                nextUpdateTime={nextUpdateTime}
                isUpdating={isUpdating}
                manualUpdate={manualUpdate}
                // DR 발령 데이터
                drHistoryLatestData={drHistoryLatestData}
                // DR 발령일 전력 수요 데이터
                powerMeterDemandDrEvent={powerMeterDemandDrEvent}
              />
              )}
              {activeTab === 'charger' && (
                <div className='flex flex-col gap-10 mb-20'>
                  <div className='primary-table'>
                    <table>
                      <thead>
                        <tr>
                          <th>번호</th>
                          <th>고유식별자</th>
                          <th>용량(kWh)</th>
                          <th>운영정보</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {station.Chargers.map((charger, index) => (
                          <tr key={charger.ChargerSysId}>
                            <td>{index + 1}</td>
                            <td>{charger.CpoChargerId}</td>
                            <td>{charger.ChargerCapacity}</td>
                            <td>
                              <div className="status-indicator" style={{ backgroundColor: getStatusInfo(getChargerStatusCategory(charger.Status)).bgColor }}>
                                <span className="status-dot" style={{ backgroundColor: getStatusInfo(getChargerStatusCategory(charger.Status)).dotColor }}></span>
                                <span className="status-text" style={{ color: getStatusInfo(getChargerStatusCategory(charger.Status)).color }}>{getStatusInfo(getChargerStatusCategory(charger.Status)).text}</span>
                              </div>
                            </td>
                            <td>
                              <p className="text-gray-600 cursor-pointer text-underline" onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/charger-management/detail/${charger.ChargerSysId}`, { state: { from: 'detail' } });
                              }}>자세히보기</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* 수정 모달 */}
        {editModalOn && (
          <StationUpdateSideModal 
            onClose={() => setEditModalOn(false)} 
            target={station} 
            onSuccess={onEditSuccess} 
          />
        )}
        
        {/* 삭제 모달 */}
        {deleteModalOn && (
          <StationDeleteModal 
            onClose={() => setDeleteModalOn(false)} 
            station={station} 
            onDeleteSuccess={onDeleteSuccess} 
          />
        )}
    </div>
  );
};

export default StationDetail;
